import api from './api';

// Tipos baseados na documentação da API
export type UserRole = 'ADMIN' | 'GERENTE' | 'OPERADOR' | 'VENDEDOR' | 'ESTOQUISTA' | 'FINANCEIRO' | 'COMPRADOR';

export interface User {
  id: string;
  username: string;
  email: string;
  nome: string;
  sobrenome: string;
  telefone: string;
  emailVerificado: boolean;
  ativo: boolean;
  roles: UserRole[];
  ultimoLogin?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserRequest {
  username: string;
  email: string;
  password: string;
  nome: string;
  sobrenome: string;
  telefone: string;
  emailVerificado: boolean;
  roles: UserRole[];
}

export interface UpdateUserRequest {
  username?: string;
  email?: string;
  nome?: string;
  sobrenome?: string;
  telefone?: string;
  emailVerificado?: boolean;
  ativo?: boolean;
  roles?: UserRole[];
  password?: string;
}

export interface ChangePasswordRequest {
  senhaAtual: string;
  novaSenha: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  timestamp: string;
}

export interface PagedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
}

export interface UserSearchParams {
  page?: number;
  size?: number;
  search?: string;
  role?: UserRole;
  ativo?: boolean;
}

class UserService {
  private readonly baseUrl = 'v1/usuarios';

  // Removido dados mockados - sempre usar API real

  // Listar usuários com paginação
  async getUsers(params: UserSearchParams = {}): Promise<ApiResponse<PagedResponse<User>>> {
    const queryParams = new URLSearchParams();
    
    if (params.page !== undefined) queryParams.append('page', params.page.toString());
    if (params.size !== undefined) queryParams.append('size', params.size.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.role) queryParams.append('role', params.role);
    if (params.ativo !== undefined) queryParams.append('ativo', params.ativo.toString());
    
    const response = await api.get(`${this.baseUrl}?${queryParams}`);
    console.log('🔍 UserService - Resposta da API getUsers:', response.data);
    
    // Mapear o campo 'active' para 'ativo' se necessário
    if (response.data?.data?.content) {
      response.data.data.content = response.data.data.content.map((user: any) => ({
        ...user,
        ativo: user.ativo !== undefined ? user.ativo : user.active
      }));
      console.log('🔍 UserService - Usuários após mapeamento:', response.data.data.content.map((u: User) => ({ id: u.id, nome: u.nome, ativo: u.ativo })));
    }
    return response.data;
  }

  // Buscar usuário por ID
  async getUserById(id: string): Promise<ApiResponse<User>> {
    const response = await api.get(`${this.baseUrl}/${id}`);
    return response.data;
  }

  // Criar novo usuário
  async createUser(userData: CreateUserRequest): Promise<ApiResponse<User>> {
    const response = await api.post(this.baseUrl, userData);
    return response.data;
  }

  // Atualizar usuário
  async updateUser(id: string, userData: UpdateUserRequest): Promise<ApiResponse<User>> {
    const response = await api.put(`${this.baseUrl}/${id}`, userData);
    return response.data;
  }

  // Deletar usuário
  async deleteUser(id: string): Promise<ApiResponse<void>> {
    const response = await api.delete(`${this.baseUrl}/${id}`);
    return response.data;
  }

  // Ativar usuário
  async activateUser(id: string): Promise<ApiResponse<User>> {
    console.log('🔍 UserService - activateUser chamado para ID:', id);
    const response = await api.patch(`${this.baseUrl}/${id}/ativar`);
    console.log('🔍 UserService - Resposta da API activateUser:', response.data);
    
    // Mapear o campo 'active' para 'ativo' se necessário
    if (response.data?.data) {
      response.data.data = {
        ...response.data.data,
        ativo: response.data.data.ativo !== undefined ? response.data.data.ativo : response.data.data.active
      };
      console.log('🔍 UserService - Usuário após ativação e mapeamento:', { id: response.data.data.id, nome: response.data.data.nome, ativo: response.data.data.ativo });
    }
    return response.data;
  }

  // Desativar usuário
  async deactivateUser(id: string): Promise<ApiResponse<User>> {
    console.log('🔍 UserService - deactivateUser chamado para ID:', id);
    const response = await api.patch(`${this.baseUrl}/${id}/desativar`);
    console.log('🔍 UserService - Resposta da API deactivateUser:', response.data);
    
    // Mapear o campo 'active' para 'ativo' se necessário
    if (response.data?.data) {
      response.data.data = {
        ...response.data.data,
        ativo: response.data.data.ativo !== undefined ? response.data.data.ativo : response.data.data.active
      };
      console.log('🔍 UserService - Usuário após desativação e mapeamento:', { id: response.data.data.id, nome: response.data.data.nome, ativo: response.data.data.ativo });
    }
    return response.data;
  }

  // Alterar senha do usuário
  async changePassword(id: string, passwordData: ChangePasswordRequest): Promise<ApiResponse<void>> {
    try {
      const response = await api.patch(`${this.baseUrl}/${id}/senha`, passwordData);
      return response.data;
    } catch (error) {
      console.warn('API não disponível, simulando alteração de senha:', error);
      return {
        success: true,
        message: 'Senha alterada com sucesso',
        data: undefined as any,
        timestamp: new Date().toISOString()
      };
    }
  }

  // Alterar senha via administração (apenas novaSenha)
  async updatePassword(id: string, novaSenha: string): Promise<ApiResponse<void>> {
    const payload = { novaSenha } as any;
    const response = await api.patch(`${this.baseUrl}/${id}/senha`, payload);
    return response.data;
  }

  // Buscar usuários ativos
  async getActiveUsers(): Promise<ApiResponse<User[]>> {
    const response = await api.get(`${this.baseUrl}/ativos`);
    return response.data;
  }

  // Buscar usuário por email
  async getUserByEmail(email: string): Promise<ApiResponse<User>> {
    const response = await api.get(`${this.baseUrl}/email/${email}`);
    return response.data;
  }

  // Buscar usuários por role
  async getUsersByRole(role: UserRole): Promise<ApiResponse<User[]>> {
    const response = await api.get(`${this.baseUrl}/role/${role}`);
    return response.data;
  }

  // Obter todas as roles disponíveis
  async getRoles(): Promise<ApiResponse<UserRole[]>> {
    const response = await api.get(`${this.baseUrl}/roles`);
    return response.data;
  }

  // Validar email
  async validateEmail(email: string, userId?: string): Promise<ApiResponse<{ valid: boolean }>> {
    const params = new URLSearchParams({ email });
    if (userId) params.append('usuarioId', userId);
    const response = await api.get(`${this.baseUrl}/validar-email?${params}`);
    return response.data;
  }

  // Validar username
  async validateUsername(username: string, userId?: string): Promise<ApiResponse<{ valid: boolean }>> {
    const params = new URLSearchParams({ username });
    if (userId) params.append('usuarioId', userId);
    const response = await api.get(`${this.baseUrl}/validar-username?${params}`);
    return response.data;
  }

  // Obter estatísticas de usuários
  async getUserStatistics(): Promise<ApiResponse<any>> {
    const response = await api.get(`${this.baseUrl}/estatisticas`);
    return response.data;
  }

  // Vincular empresas a um usuário
  async linkCompanies(usuarioId: string, empresaIds: string[]): Promise<ApiResponse<void>> {
    // API espera exatamente a chave "empresaIds"
    const payload = { empresaIds } as any;
    const response = await api.post(`${this.baseUrl}/${usuarioId}/empresas`, payload);
    return response.data;
  }
}

export const userService = new UserService();
export default userService;